import {useQuery} from "@tanstack/react-query";
import {
    getCancelledClassesForUser,
    getFutureClassesForUser2,
    getPastClassesForUser,
    getPendingClassesForUser,
} from "api/booking.api.js";
import {useEffect, useState} from "react";
import {useLocation} from "react-router-dom";
import styled from "styled-components";
import {convertHexToRGBA, defaultThemeColors, fonts, mediaSizes} from "ui/theme";
import {PastClasses} from "./section/PastClasses.jsx";
import {UpcomingClasses} from "./section/UpcomingClasses.jsx";
import {CancelledClasses} from "./section/CancelledClasses";

export const MyClasses = () => {
    const {state} = useLocation();

    const defaultValue =
        window.innerWidth >= +mediaSizes.md.replace('px', '')
            ? "upcoming"
            : null

    const [activeTab, setActiveTab] = useState(defaultValue);

    useEffect(() => {
        getActiveTab(state);
    }, [state]);

    const getActiveTab = (currentState) => {
        switch (currentState) {
            case "class_request_accepted":
                setActiveTab("upcoming");
                break;

            case "cancellation_of_booking_by_instructor":
                setActiveTab("cancelled");
                break;

            case "reschedule_request_accepted":
                setActiveTab("upcoming");
                break;

            case "reschedule_request_rejected":
                setActiveTab("upcoming");
                break;

            case "class_request_rejected":
                setActiveTab("cancelled");
                break;

            // After sending a booking request: open the requests list.
            case "class_requests":
                setActiveTab("upcoming");
                break;

            default:
                setActiveTab(defaultValue);
                break;
        }
    };

    const {data: futureClasses, isLoading: isFutureClassesLoading} = useQuery({
        queryKey: ["getFutureClassesForUser2"],
        queryFn: getFutureClassesForUser2,
    });

    const {data: pendingClasses, isLoading: isPendingClassesLoading} = useQuery({
        queryKey: ["getPendingClassesForUser"],
        queryFn: getPendingClassesForUser,
    });

    const {data: pastClasses, isLoading: isPastClassesLoading} = useQuery({
        queryKey: ["getPastClassesForUser"],
        queryFn: getPastClassesForUser,
    });

    const {data: cancelledClasses, isLoading: isCancelledClassesLoading} = useQuery({
        queryKey: ["getCancelledClassesForUser"],
        queryFn: getCancelledClassesForUser,
    });

    const setTabToNull = () => setActiveTab(null)

    return (
        <Container>
            <Sidebar isTabActive={!!activeTab}>
                <Title>My Classes</Title>
                <SidebarItem
                    active={activeTab === "upcoming"}
                    onClick={() => setActiveTab("upcoming")}
                >
                    Upcoming Classes
                </SidebarItem>
                <SidebarItem
                    active={activeTab === "past"}
                    onClick={() => setActiveTab("past")}
                >
                    Past Classes
                </SidebarItem>
                <SidebarItem
                    active={activeTab === "cancelled"}
                    onClick={() => setActiveTab("cancelled")}
                >
                    Cancelled Classes
                </SidebarItem>
            </Sidebar>
            <Content>
                <SidebarContent active={activeTab === "upcoming"}>
                    <UpcomingClasses
                        initialTab={state === "class_requests" ? "2" : "1"}
                        futureClasses={futureClasses?.data || []}
                        isFutureClassesLoading={isFutureClassesLoading}
                        pendingClasses={pendingClasses?.data || []}
                        isPendingClassesLoading={isPendingClassesLoading}
                        setTabToNull={setTabToNull}
                    />
                </SidebarContent>
                <SidebarContent active={activeTab === "past"}>
                    <PastClasses
                        pastClasses={pastClasses?.data || []}
                        isPastClassesLoading={isPastClassesLoading}
                        setTabToNull={setTabToNull}
                    />
                </SidebarContent>
                <SidebarContent active={activeTab === "cancelled"}>
                    <CancelledClasses
                        cancelledClasses={cancelledClasses?.data || []}
                        isCancelledClassesLoading={isCancelledClassesLoading}
                        setTabToNull={setTabToNull}
                    />
                </SidebarContent>
            </Content>
        </Container>
    );
};

const Container = styled.div`
    max-width: 1300px;
    margin-inline: auto;
    padding-block: 2rem;
    padding-bottom: 1rem;
    padding-inline: 1rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    font-family: var(--font-secondary);
    min-height: var(--page-height);

    @media (width >= ${mediaSizes.md}) {
        flex-direction: row;
        gap: 2rem;
        padding-inline: 2rem;
    }
    @media (width >= ${mediaSizes.lg}) {
        gap: 4rem;
    }
`;

const Sidebar = styled.div`
    flex-direction: column;
    gap: 1rem;
    min-width: 200px;
    display: ${({isTabActive}) => (isTabActive ? "none" : "flex")};
    @media (width >= ${mediaSizes.md}) {
        display: flex;
    }
`;

const Title = styled.h1`
    font-size: ${fonts.sizes.big};
    font-weight: 600;

    @media (width >= ${mediaSizes.md}) {
        font-size: ${fonts.sizes.h4};
    }
    @media (width >= ${mediaSizes.lg}) {
        font-size: ${fonts.sizes["h3.4"]};
    }
`;

const SidebarItem = styled.button`
    background: ${({active}) =>
            active ? convertHexToRGBA(defaultThemeColors.blue, 0.05) : "none"};
    color: ${({active}) =>
            active ? defaultThemeColors.blue : defaultThemeColors.txt};
    border: none;
    padding: 1rem;
    text-align: left;
    cursor: pointer;
    border-radius: 8px;
    font-family: Montserrat;
    font-size: ${fonts.sizes.regular};
    font-weight: 600;

    &:hover {
        background-color: ${convertHexToRGBA(defaultThemeColors.blue, 0.05)};
        color: ${defaultThemeColors.blue};
    }
`;

const Content = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
`;

const SidebarContent = styled.div`
    display: ${({active}) => (active ? "flex" : "none")};
    flex-grow: 1;
    height: 100%;
    flex-direction: column;
    width: 100%;
`;

const HeaderTitleContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 1.2rem;
    margin-bottom: 1.8rem;
    @media (width >= ${mediaSizes.md}) {
        display: none;
    }
`

const HeaderTitle = styled.div`
    font-size: ${fonts.sizes.big};
    font-weight: 600;

    @media (width >= ${mediaSizes.md}) {
        font-size: ${fonts.sizes.h4};
    }
    @media (width >= ${mediaSizes.lg}) {
        font-size: ${fonts.sizes["h3.4"]};
    }
`
